import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Lock, ArrowRight, Globe } from "lucide-react";
import { LogoLockup } from "./LogoMark";
import { useLang } from "../i18n/LanguageContext";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Residential", to: "/residential" },
  { label: "Commercial", to: "/commercial" },
  { label: "Services", to: "/services" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const { lang, toggle, t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <>
      <div className="fixed top-0 inset-x-0 z-50 flex justify-center px-4 pt-4 md:pt-5">
        <motion.header
          data-testid="site-header"
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className={`w-full max-w-6xl rounded-full transition-all duration-500 ${
            scrolled ? "glass-strong shadow-2xl shadow-black/40" : "bg-transparent border border-transparent"
          }`}
        >
          <div className="flex items-center justify-between pl-5 pr-3 py-2.5">
            <Link to="/" data-testid="header-logo-link">
              <LogoLockup dark />
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={`text-sm font-500 px-3 py-2 rounded-full transition-all ${
                    loc.pathname === n.to ? "text-white bg-white/10" : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {t(n.label)}
                </Link>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-2">
              <button
                data-testid="lang-toggle"
                onClick={toggle}
                aria-label="Change language"
                className="flex items-center gap-1.5 text-sm font-600 text-white/60 hover:text-white transition-colors px-2.5 py-2 rounded-full hover:bg-white/5"
              >
                <Globe className="h-3.5 w-3.5" /> {lang === "fr" ? "EN" : "FR"}
              </button>
              <button
                data-testid="client-portal-btn"
                onClick={() => nav("/portal")}
                className="flex items-center gap-2 text-sm font-500 text-white/60 hover:text-white transition-colors px-3 py-2"
              >
                <Lock className="h-3.5 w-3.5" /> {t("Portal")}
              </button>
              <button
                data-testid="header-quote-btn"
                onClick={() => nav("/quote")}
                className="btn-pill group bg-white text-ink-950 pl-5 pr-4 py-2.5 text-sm font-600 flex items-center gap-1.5 hover:shadow-lg hover:shadow-white/20"
              >
                {t("Get a Quote")}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>

            <div className="flex items-center gap-1 lg:hidden">
              <button
                data-testid="lang-toggle-mobile"
                onClick={toggle}
                aria-label="Change language"
                className="flex items-center gap-1 text-sm font-600 text-white/70 px-2 py-2"
              >
                <Globe className="h-4 w-4" /> {lang === "fr" ? "EN" : "FR"}
              </button>
              <button
                data-testid="mobile-menu-btn"
                className="text-white p-2"
                onClick={() => setOpen((o) => !o)}
                aria-label="Toggle menu"
              >
                {open ? <X /> : <Menu />}
              </button>
            </div>
          </div>
        </motion.header>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 glass-strong pt-28 px-8 lg:hidden"
          >
            <nav className="flex flex-col gap-2">
              {NAV.map((n, i) => (
                <motion.div key={n.to} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <Link to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                    className="font-serif text-4xl text-white/90 hover:text-white block py-1">{t(n.label)}</Link>
                </motion.div>
              ))}
              <button onClick={() => nav("/quote")} data-testid="mobile-quote-btn"
                className="btn-pill mt-6 bg-white text-ink-950 px-6 py-4 text-sm font-600 self-start">
                {t("Request a Quote")}
              </button>
              <button onClick={() => { setOpen(false); nav("/portal"); }}
                className="flex items-center gap-2 text-white/50 text-sm mt-3">
                <Lock className="h-4 w-4" /> {t("Client Portal")}
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
